import React from 'react';
import { DatePicker } from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useTranslation } from 'react-i18next';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);
dayjs.extend(utc);

const { RangePicker } = DatePicker;

const formatOptions = [
  'DD/MM/YYYY',
  'D/M/YYYY',
  'D/MM/YYYY',
  'DD/M/YYYY',
  'DD-MM-YYYY',
  'D-M-YYYY',
  'D-MM-YYYY',
  'DD-M-YYYY'
];

export type UTCNoShiftRangePickerProps = Omit<RangePickerProps, 'onChange' | 'picker' | 'showTime'> & {
  onChangeUTC?: (dates: [Date | null, Date | null] | null) => void;
  /** onChange gốc của AntD (tùy chọn) */
  onChange?: RangePickerProps['onChange'];
};

/** Dựng Date UTC thật (serialize ra ...Z) từ parts */
export function buildUTCDateFromParts(src: Dayjs): Date {
  return new Date(
    Date.UTC(src.year(), src.month(), src.date(), src.hour(), src.minute(), src.second(), src.millisecond())
  );
}

const UTCNoShiftRangePicker: React.FC<UTCNoShiftRangePickerProps> = ({ onChangeUTC, onChange, ...rest }) => {
  const { t } = useTranslation();

  const handleChange: RangePickerProps['onChange'] = (values, formatString) => {
    // forward onChange gốc (nếu cần)
    onChange?.(values, formatString);

    if (!values || values.length !== 2) {
      onChangeUTC?.(null);
      return;
    }

    const [startDate, endDate] = values;

    let startUTC: Date | null = null;
    let endUTC: Date | null = null;

    if (startDate) {
      // Ép về đầu ngày local (00:00:00)
      const startLocal = startDate.startOf('day');
      startUTC = buildUTCDateFromParts(startLocal);
    }

    if (endDate) {
      // Ép về đầu ngày local (00:00:00)
      const endLocal = endDate.startOf('day');
      endUTC = buildUTCDateFromParts(endLocal);
    }

    onChangeUTC?.([startUTC, endUTC]);
  };

  return (
    <RangePicker
      format={formatOptions}
      inputReadOnly={false}
      onChange={handleChange}
      placeholder={[t('placeholder.base.fDate').toString(), t('placeholder.base.tDate').toString()]}
      {...rest}
    />
  );
};

export default UTCNoShiftRangePicker;
